import {
  defaultTimezone,
  SSR_V0_RELEASE_DATE_UNIX_TS,
  websiteStatus
} from '@va/constants';
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { getWebsite } from '.';

/**
 * TODO Move all selectors to ./new.ts
 */

export const getFormValue = (state, formName, valueName) =>
  state.getIn(['core', 'form', formName, 'values', valueName]);

export const isPremium = (state) => {
  const status = state.getIn(['core', 'website', 'status']);
  // Force websites with no platform selected to be 'premium' in order to not show upgrade buttons/
  if (status === websiteStatus.PENDING) return true;

  const isPayable = state.getIn(['core', 'website', 'isPayable'], false);
  return !!isPayable;
};

export const hasWebsite = (state) => state.getIn(['core', 'website', 'exists'], false);

export const getSsrSettingsSelector = createSelector(
  [(state) => state.getIn(['core', 'api', 'getSsrSettings'], Immutable.Map()).toJS()],
  (ssrSettings) => ssrSettings,
);


export const websiteIsSumoActive = createSelector(
  [(state) => state.getIn(['core', 'website'], Immutable.Map())],
  (website) => {
    return website.get('sumoActive', false);
  },
);

export const getWebsiteSumoLimit = createSelector(
  [(state) => state.getIn(['core', 'website'], Immutable.Map())],
  (website) => {
    return website.get('sumoLimit', null);
  },
);

export const selectWebsiteMonthlyVisits = createSelector(
  [(state) => state.getIn(['core', 'website'], Immutable.Map())],
  (website) => {
    return website.get('numberOfMonthlyVisits', null);
  },
);

export const hasData = createSelector(
  [(state) => state.getIn(['core', 'website'], Immutable.Map()).toJS()],
  (website) => {
    if (!website.lastVisitTs) return false;

    if (website.lastResetDate) {
      return website.lastVisitTs > website.lastResetDate;
    }

    return true;
  },
);

export const hasFirstLogin = (state) => state.getIn(['core', 'website', 'hasFirstLogin'], false);

export const getWebsiteTimezone = createSelector(
  [(state) => state.getIn(['core', 'website', 'timezone'], defaultTimezone)],
  (timezone) => timezone,
);

export const getWebsiteUrl = createSelector(
  [
    (state) => state.getIn(['core', 'api', 'settings', 'url']),
    (state) => state.getIn(['core', 'website', 'url']),
    (state) => state.getIn(['core', 'app', 'user', 'siteUrl'], '#'),
  ],
  (urlFromSettings, urlFromWebsite, userUrl) => {
    if (urlFromSettings && urlFromSettings !== 'null') return urlFromSettings;
    if (urlFromWebsite && urlFromWebsite !== 'null') return urlFromWebsite;
    return userUrl;
  },
);

export const getWebsiteCountryISO = createSelector(getWebsite, (website) => website.ipCountryISO);

export const getAuthentication = (state) => {
  let auth = state.getIn(
    ['core', 'authentication'],
    Immutable.Map({
      inProgress: false,
      token: null,
      refreshToken: null,
      instanceId: null,
    }),
  );
  return auth.toJS();
};

export const isImpersonator = (state) => {
  const auth = getAuthentication(state);

  return auth.impersonateToken || false;
};

export const hideSSrWarningSign = createSelector([getWebsite], (website) => {
  const fFlags = website.frontendFlags;
  return !!(fFlags && fFlags.dontShowSsrWarningSign);
});

export const hadRecordings = createSelector([getWebsite], (website) => {
  const fFlags = website.frontendFlags;
  return !!(fFlags && fFlags.hadRecordings);
});

export const wasInstalledAfterSsrV0 = createSelector(
  [getWebsite],
  (website) => (website.firstPremiumTs || website.installDate) >= SSR_V0_RELEASE_DATE_UNIX_TS,
);

export const getSsrCodeInstalledFlag = (state) =>
  state.getIn(['core', 'website', 'frontendFlags', 'ssrCodeInstalled'], null);

export const isEventTrackingEnabled = createSelector([getWebsite], (website) => {
  return !!website?.frontendFlags?.startedEventTracking;
});
